import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Check } from 'lucide-react';

const FoodTrucks = () => {
  const foodTrucks = [
    {
      name: 'Burger & Grill Truck',
      description: 'Saftige Burger, Grillspezialitäten und Pommes vom Feinsten',
      image: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=800&h=600&fit=crop',
      tags: ['Burger', 'Grill', 'Pommes'],
      features: ['100% Rindfleisch', 'Vegetarische Optionen', 'Hausgemachte Saucen', 'Frische Zutaten']
    },
    {
      name: 'Pizza & Pasta Truck',
      description: 'Authentische italienische Küche mit frischen Zutaten',
      image: 'https://images.unsplash.com/photo-1513104890138-7c749659a591?w=800&h=600&fit=crop',
      tags: ['Pizza', 'Pasta', 'Italienisch'],
      features: ['Steinofen-Pizza', 'Frische Pasta', 'Italienische Spezialitäten', 'Vegetarisch & Vegan']
    },
    {
      name: 'Asian Street Food',
      description: 'Asiatische Köstlichkeiten – von Thai bis Sushi',
      image: 'https://images.unsplash.com/photo-1563379091339-03b21ab4a4f8?w=800&h=600&fit=crop',
      tags: ['Asiatisch', 'Thai', 'Sushi'],
      features: ['Thai-Curry', 'Gebratene Nudeln', 'Frühlingsrollen', 'Vegetarische Optionen']
    },
    {
      name: 'Tex-Mex Truck',
      description: 'Tacos, Burritos und mexikanische Leckereien',
      image: 'https://images.unsplash.com/photo-1565299585323-38d6b0865b47?w=800&h=600&fit=crop',
      tags: ['Mexikanisch', 'Tacos', 'Burritos'],
      features: ['Frische Tacos', 'Große Burritos', 'Hausgemachte Salsa', 'Vegane Optionen']
    },
    {
      name: 'Softeis Truck',
      description: 'Cremiges Softeis und süße Leckereien',
      image: 'https://images.unsplash.com/photo-1563805042-7684c019e1cb?w=800&h=600&fit=crop',
      tags: ['Softeis', 'Dessert', 'Süßes'],
      features: ['Frisches Softeis', 'Crepes', 'Waffeln', 'Churros Bus']
    },
    {
      name: 'Cocktail & Bar Bus',
      description: 'VW T2 Bus mit professioneller Bar-Ausstattung',
      image: 'https://customer-assets.emergentagent.com/job_event-truck/artifacts/s2z0yhlq_A8544B8F-4AB1-4DC3-8B3A-367E3D54C3C4.JPG',
      tags: ['Cocktails', 'Getränke', 'Bar'],
      features: ['Klassische Cocktails', 'Bier Catering', 'Softdrinks', 'Professionelle Barkeeper']
    }
  ];

  const benefits = [
    'Vielfältige Auswahl an kulinarischen Köstlichkeiten',
    'Vegetarische und vegane Optionen verfügbar',
    'Professionelle Catering-Partner',
    'Flexible Menü-Anpassungen möglich',
    'Komplette Ausstattung und Servicepersonal',
    'Perfekt für Events von 50 bis 3.000 Gästen'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1556906397-e87f93c7b7e1?w=1920&h=1080&fit=crop)',
            filter: 'brightness(0.4)'
          }}
        />
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">
            Unsere <span className="text-red-600">Food Trucks</span>
          </h1>
          <p className="text-xl md:text-2xl font-light">
            Kulinarische Vielfalt für jedes Event
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <div className="bg-red-600 rounded-full p-1 mt-1">
                  <Check className="w-4 h-4 text-white" />
                </div>
                <span className="text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Food Trucks Grid */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {foodTrucks.map((truck, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={truck.image}
                    alt={truck.name}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold mb-2 text-gray-900">{truck.name}</h3>
                  <p className="text-gray-600 mb-4">{truck.description}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {truck.tags.map((tag, tagIndex) => (
                      <Badge key={tagIndex} variant="secondary" className="bg-red-50 text-red-600">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  <div className="space-y-2">
                    {truck.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center gap-2 text-sm text-gray-600">
                        <Check className="w-4 h-4 text-red-600" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Special Services */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6 text-gray-900">
            Noch mehr <span className="text-red-600">Möglichkeiten</span>
          </h2>
          <p className="text-xl text-gray-600 mb-12">
            Neben unseren Food Trucks bieten wir auch spezielle Event-Lösungen:
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-6 bg-gray-50 rounded-lg">
              <div className="text-4xl mb-4">🍹</div>
              <h3 className="text-xl font-bold mb-2">VW T2 Cocktailbar</h3>
              <p className="text-gray-600">Stylische Bar für Cocktails und Getränke</p>
            </div>
            <div className="p-6 bg-gray-50 rounded-lg">
              <div className="text-4xl mb-4">📸</div>
              <h3 className="text-xl font-bold mb-2">Photobus</h3>
              <p className="text-gray-600">Mobile Fotobox für unvergessliche Momente</p>
            </div>
            <div className="p-6 bg-gray-50 rounded-lg">
              <div className="text-4xl mb-4">🎵</div>
              <h3 className="text-xl font-bold mb-2">Live Entertainment</h3>
              <p className="text-gray-600">DJs, Bands und Künstler</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-red-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">
            Bereit für Ihr Food Truck Event?
          </h2>
          <p className="text-xl mb-10">
            Kontaktieren Sie uns für ein individuelles Angebot
          </p>
          <Link to="/contact">
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-red-600 px-12 py-6 text-lg">
              Jetzt Angebot anfordern
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default FoodTrucks;